// src/services/workspaceApi.ts
import apiClient from "./apiClient";

/**
 * A business the user can work in: their own, or an employer's (when they are
 * an employee who also runs their own business). The id is the owner's user id
 * and is sent to the API as the X-Workspace-Id header.
 */
export type Workspace = {
  id: string;
  name: string;
  role: "owner" | "admin" | "employee";
  isOwn: boolean;
  /** Currency the business keeps its prices in, e.g. "USD". */
  currency: string;
};

export const ACTIVE_WORKSPACE_KEY = "active_workspace_id";

export const getActiveWorkspaceId = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_WORKSPACE_KEY);
  } catch {
    return null;
  }
};

export const setActiveWorkspaceId = (workspaceId: string | null) => {
  try {
    if (workspaceId) localStorage.setItem(ACTIVE_WORKSPACE_KEY, workspaceId);
    else localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
  } catch {
    // storage unavailable: the API falls back to the default business
  }
};

export const fetchWorkspaces = async (): Promise<{
  workspaces: Workspace[];
  defaultWorkspaceId: string | null;
}> => {
  // The profile doesn't depend on the active business, so send none.
  const { data } = await apiClient.get("/auth/me", {
    headers: { "X-Workspace-Id": "" },
  });
  const workspaces: Workspace[] = Array.isArray(data?.workspaces)
    ? data.workspaces
        .filter((w: any) => w && typeof w.id === "string")
        .map((w: any) => ({
          id: w.id,
          name: String(w.name || "Business"),
          role: w.role || "owner",
          isOwn: !!w.isOwn,
          currency: typeof w.currency === "string" ? w.currency : "USD",
        }))
    : [];
  return { workspaces, defaultWorkspaceId: data?.defaultWorkspaceId ?? null };
};
